import { Provider } from 'react-redux';
import { createGlobalStyle } from 'styled-components';

import store from 'src/utils/store';
import fontFaces from 'src/utils/fontFaces';

import App from 'src/components/App';

// Soft CSS reset + design tokens + global styles
const GlobalStyles = createGlobalStyle`
    *, *::before, *::after {
        box-sizing: border-box;
    }

    * {
        margin: 0;
    }

    html, body {
        height: 100%;
    }

    body {
        -webkit-font-smoothing: antialiased;
        background: var(--background);
        font: 400 14px/1.4 'Proxima Nova', 'Open Sans', 'Gill Sans MT', 'Gill Sans', Arial, sans-serif;
        color: var(--text-primary);
    }

    img, picture, video, canvas, svg {
        display: block;
        max-width: 100%;
    }

    input, button, textarea, select {
        font: inherit;
    }

    p, h1, h2, h3, h4, h5, h6 {
        overflow-wrap: break-word;
    }

    #root {
        isolation: isolate;
    }

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: var(--surface-raised);
    }

    &::-webkit-scrollbar-thumb {
        background: var(--neutral);
    }

    &::-webkit-scrollbar-thumb:hover {
        background: var(--text-disabled);
    }

    details summary::-webkit-details-marker {
        display: none;
    }

    /* Palette — raw color values, theme-independent */
    :root {
        --viewport-height: 100vh;
        --denim-blue: #002b50;
        --green-1: #15c8d1;
        --green-2: #17d7d8;
        --green-3: #18e9da;
        --green-4: #17f1d8;
        --green-5: #3cf7dc;
        --green-6: #4dffe1;
        --green-7: #83ffe7;
        --green-8: #a4ffee;
        --grey-1: #3a4355;
        --grey-3: #5d6b84;
        --grey-4: #ced4da;
        --grey-5: #bac9de;
        --grey-7: #f0f5fc;
        --grey-8: #f9fbff;
        --poki-blue: #009cff;
        --poki-hover: #0097f5;
        --poki-hover-1: #005f99;
        --purple-1: #c977e8;
        --purple-2: #d490f2;
        --purple-3: #da9ef3;
        --purple-4: #dca7f2;
        --purple-5: #e0aef5;
        --purple-6: #e1b5f7;
        --purple-7: #eac0fc;
        --purple-8: #ebccff;
        --rose-1: #ff7690;
        --rose-2: #ff8aa6;
        --rose-3: #ff99b1;
        --rose-4: #ffa4ba;
        --rose-5: #ffa9be;
        --rose-6: #ffaec5;
        --rose-7: #ffc2d6;
        --rose-8: #ffc9dd;
        --yellow-1: #ff9e00;
        --yellow-2: #ffaf00;
        --yellow-3: #ffc500;
        --yellow-4: #ffd100;
        --yellow-5: #ffdc00;
        --yellow-6: #ffe200;
        --yellow-7: #faeb59;
        --yellow-8: #ffed85;
        --data-blue-1: #D4EFFF;
        --data-blue-2: #ABD2EE;
        --data-blue-3: #83B8DD;
        --data-blue-4: #5E9DCC;
        --data-blue-5: #3684BC;
        --data-green-1: #D2F3DE;
        --data-green-2: #AEE9C3;
        --data-green-3: #61D291;
        --data-green-4: #31C67A;
        --data-green-5: #4EAC7C;
        --data-purple-1: #F6E6FF;
        --data-purple-3: #B98AE0;
        --data-purple-4: #9861D1;
        --data-purple-5: #753BC3;
        --data-red-1: #FFE1DF;
        --data-red-2: #FFB9B5;
        --data-red-3: #FF918E;
        --data-red-4: #FF696B;
        --data-red-5: #F83C4B;
        --data-orange-1: #FFE5D2;
        --data-orange-2: #FFCCA8;
        --data-orange-3: #FFB481;
        --data-orange-4: #FF9B5E;
        --data-orange-5: #FF823D;
        --data-yellow-1: #F8EACD;
        --data-yellow-2: #FDE3A9;
        --data-yellow-3: #FFDD85;
        --data-yellow-4: #FFD763;
        --data-yellow-5: #FFD03D;
        --data-yellow-7: #FFFBCD;
        --data-grey-1: #DEE1E5;
        --data-grey-3: #ADB5BD;
        --data-grey-4: #5D6B84;
        --data-grey-5: #495057;
        --data-grey-7: #EEF3FD;
        --data-grey-8: #F9FBFF;
    }

    /* Semantic tokens — light theme (default). Override in [data-theme="dark"]. */
    :root,
    [data-theme="light"] {
        --background: #EEF3FD;
        --header-background: var(--yellow-7);
        --header-text: var(--denim-blue);
        --surface: #fff;
        --surface-raised: var(--grey-8);
        --surface-muted: var(--grey-7);
        --text-primary: var(--grey-1);
        --text-secondary: var(--grey-3);
        --text-disabled: var(--grey-5);
        --neutral: var(--grey-4);
        --border-default: var(--grey-5);
        --border-subtle: var(--grey-8);
        --border-faint: var(--grey-7);
        --box-shadow: 0px 4px 8px rgb(9 30 66 / 7%), 0px 0px 2px rgb(9 30 66 / 7%);
        --purple-1: var(--data-purple-1);
        --blue-1: var(--data-blue-1);
        --green-1: var(--data-green-1);
        --orange-1: var(--data-orange-1);
    }

    [data-theme="dark"] {
        --background: #1E2038;
        --header-background: var(--denim-blue);
        --header-text: #fff;
        --surface: #343950;
        --surface-raised: #1f2128;
        --surface-muted: #2a2d36;
        --text-primary: #e8eef8;
        --text-secondary: #a8b3c5;
        --text-disabled: #5a6478;
        --neutral: #3d4452;
        --border-default: #3d4452;
        --border-subtle: var(--grey-1);
        --border-faint: var(--grey-1);
        --box-shadow: 0px 4px 8px rgb(9 30 66 / 35%), 0px 0px 2px rgb(9 30 66 / 45%);
        --purple-1: var(--data-purple-3);
        --blue-1: var(--data-blue-4);
        --green-1: var(--data-green-4);
        --orange-1: var(--data-orange-4);
    }

    @supports (height: -webkit-fill-available) {
        :root {
            --viewport-height: -webkit-fill-available;
        }
    }

    @supports (height: 100svh) {
        :root {
            --viewport-height: 100svh;
        }
    }
`;

export default function Root() {
    return (
        <>
            <style>{fontFaces}</style>
            <GlobalStyles />
            <Provider store={store}>
                <App />
            </Provider>
        </>
    );
}
